import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/payments';
const FILE_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Urgency helpers — same logic as the member-facing Payment Advice,
// so Finance sees exactly the same relative urgency members do. ──────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}
function urgencyInfo(days, isArrears, arrearsAmount) {
  if (isArrears) return arrearsAmount > 0
    ? { label: 'OVERDUE — MIFL ARREARS', color: '#e74c3c' }
    : { label: 'CLEAR', color: '#2ecc71' };
  if (days === null) return { label: 'No fixed due date', color: '#7f8c8d' };
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: '#e74c3c' };
  if (days === 0) return { label: 'Due today', color: '#e74c3c' };
  if (days <= 5) return { label: `${days}d left — urgent`, color: '#e67e22' };
  if (days <= 14) return { label: `${days}d left`, color: '#b7950b' };
  return { label: `${days}d left`, color: '#27ae60' };
}

function Payments() {
  const [tab, setTab] = useState('slips');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [obligation, setObligation] = useState(null);
  const [loadingObligation, setLoadingObligation] = useState(false);
  const [customLines, setCustomLines] = useState([]);
  const [customForm, setCustomForm] = useState({ description: '', amount: '', due_date: '', notes: '' });
  const [customMsg, setCustomMsg] = useState('');
  const [customSaving, setCustomSaving] = useState(false);
  const [depositMember, setDepositMember] = useState('');
  const [depositYear, setDepositYear] = useState(new Date().getFullYear());
  const [depositMonth, setDepositMonth] = useState(new Date().getMonth() + 1);
  const [depositRef, setDepositRef] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [depositFile, setDepositFile] = useState(null);
  const [depositSlips, setDepositSlips] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [recoYear, setRecoYear] = useState(new Date().getFullYear());
  const [recoMonth, setRecoMonth] = useState(new Date().getMonth() + 1);
  const [csvFile, setCsvFile] = useState(null);
  const [recoResult, setRecoResult] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoHistory, setRecoHistory] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runResults, setRunResults] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [notifySending, setNotifySending] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // ── Deposit Landing Pool (Reconciliation Engine Phase 3) ─────
  const [landingDeposits, setLandingDeposits] = useState([]);
  const [landingStatusFilter, setLandingStatusFilter] = useState('pending');
  const [landingCsvYear, setLandingCsvYear] = useState(new Date().getFullYear());
  const [landingCsvMonth, setLandingCsvMonth] = useState(new Date().getMonth() + 1);
  const [landingCsvFile, setLandingCsvFile] = useState(null);
  const [landingCsvLoading, setLandingCsvLoading] = useState(false);
  const [landingCsvResult, setLandingCsvResult] = useState(null);
  const [slipMember, setSlipMember] = useState('');
  const [slipAmount, setSlipAmount] = useState('');
  const [slipDate, setSlipDate] = useState('');
  const [slipDescription, setSlipDescription] = useState('');
  const [slipSourceType, setSlipSourceType] = useState('manual');
  const [slipUploadFile, setSlipUploadFile] = useState(null);
  const [slipUploading, setSlipUploading] = useState(false);
  const [assignPicks, setAssignPicks] = useState({});
  const [allocationPreview, setAllocationPreview] = useState(null);
  const [allocationPreviewLoading, setAllocationPreviewLoading] = useState(false);
  const [allocateMode, setAllocateMode] = useState('centenary');
  const [allocating, setAllocating] = useState(false);
  const [allocateResult, setAllocateResult] = useState(null);
  const readOnly = isChairmanReadOnly();

  // ── Allocation Pool (Oct 2026) ──────────────────────────
  const [poolMembers, setPoolMembers]     = useState([]);   // members with an unallocated balance
  const [poolMemberId, setPoolMemberId]   = useState('');
  const [poolPreview, setPoolPreview]     = useState(null);
  const [poolStatement, setPoolStatement] = useState(null);
  const [poolMode, setPoolMode]           = useState('centenary');
  const [poolBusy, setPoolBusy]           = useState(false);
  const [poolPriority, setPoolPriority] = useState([]);   // line keys Finance clicked "Allocate first" on, in click order
  const [poolPage, setPoolPage]           = useState(0);
  const POOL_PAGE_SIZE = 22;   // members listed per page (001–022, then 023–044, …)
  const poolNumOf = m => { const d = String(m.member_number || '').match(/\d+/g); return d ? parseInt(d[d.length - 1], 10) : Number.MAX_SAFE_INTEGER; };

  const fetchPoolMembers = () => {
    axios.get(API + '/allocation-pool/members').then(res => setPoolMembers(res.data)).catch(() => {});
  };
  const fetchPoolPreview = (memberId, prio) => {
    const q = prio && prio.length ? '?priority=' + encodeURIComponent(prio.join(',')) : '';
    axios.get(API + '/allocation-pool/' + memberId + '/preview' + q).then(res => setPoolPreview(res.data)).catch(err => setError(err.response?.data?.error || 'Failed to load allocation preview'));
  };
  const setPriorityAndReload = (next) => { setPoolPriority(next); if (poolMemberId) fetchPoolPreview(poolMemberId, next); };
  const loadPoolMember = (memberId) => {
    setPoolMemberId(memberId); setPoolPreview(null); setPoolStatement(null); setPoolPriority([]);
    if (!memberId) return;
    fetchPoolPreview(memberId, []);
    axios.get(API + '/allocation-pool/' + memberId + '/statement').then(res => setPoolStatement(res.data)).catch(() => {});
  };
  const goToAllocationPool = (memberId) => { setTab('allocation'); loadPoolMember(String(memberId)); };

  const allocatePool = async () => {
    if (!poolPreview || poolPreview.balance <= 0) return;
    const m = members.find(x => String(x.id) === String(poolMemberId));
    if (!window.confirm(`Allocate UGX ${poolPreview.balance.toLocaleString()} for ${m ? m.full_name : 'this member'}?\n\nObligations: UGX ${poolPreview.total_allocated.toLocaleString()}\nExcess to MIFL (MCN): UGX ${poolPreview.excess.toLocaleString()}\n\nThis posts to Treasury and cannot be undone here.`)) return;
    setPoolBusy(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(API + '/allocation-pool/' + poolMemberId + '/allocate', { mode_of_payment: poolMode, priority: poolPriority });
      setSuccess(`${res.data.batch_ref}: allocated UGX ${Number(res.data.total_allocated).toLocaleString()}` +
        (res.data.excess_to_mcn > 0 ? `, excess UGX ${Number(res.data.excess_to_mcn).toLocaleString()} → MIFL credit` : '') +
        (res.data.documents?.length ? ` (${res.data.documents.join(', ')})` : ''));
      fetchPoolMembers(); loadPoolMember(poolMemberId);
    } catch (err) { setError(err.response?.data?.error || 'Allocation failed'); }
    finally { setPoolBusy(false); }
  };

  const clearPool = async () => {
    const n = poolMembers.reduce((a, m) => a + m.deposit_count, 0);
    const total = poolMembers.reduce((a, m) => a + m.balance, 0);
    if (n === 0) { setError('Nothing to clear — the pool is already empty.'); return; }
    const typed = window.prompt(`CLEAR THE ALLOCATION POOL\n\nThis retires ${n} unallocated deposit(s) — UGX ${total.toLocaleString()} across ${poolMembers.length} member(s) — so every balance goes to zero. Nothing is posted to Treasury or any obligation, and allocated deposits are untouched. You can then re-import the bank statement (everything will land as Pending).\n\nType CLEAR to confirm:`);
    if (typed !== 'CLEAR') return;
    setPoolBusy(true); setError(''); setSuccess('');
    try {
      const res = await axios.post(API + '/allocation-pool/clear', { confirm: 'CLEAR' });
      setSuccess(`Pool cleared — ${res.data.cleared} deposit(s), UGX ${Number(res.data.total_amount).toLocaleString()}, ${res.data.members} member(s). Re-import the bank statement in the Deposit Landing Pool.`);
      setPoolMemberId(''); setPoolPreview(null); setPoolStatement(null);
      fetchPoolMembers();
    } catch (err) { setError(err.response?.data?.error || 'Clear failed'); }
    finally { setPoolBusy(false); }
  };

  const exportStatementPDF = () => {
    if (!poolStatement) return;
    const m = members.find(x => String(x.id) === String(poolMemberId));
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text('KIGEZI ASSOCIATION — MEMBER DEPOSIT STATEMENT', 105, 16, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Member: ${m ? m.full_name + ' (' + m.member_number + ')' : poolMemberId}`, 14, 26);
    doc.text(`Printed: ${new Date().toISOString().slice(0, 10)}`, 196, 26, { align: 'right' });
    autoTable(doc, {
      startY: 32,
      head: [['Date', 'Ref', 'Details', 'In', 'Out', 'Balance']],
      body: poolStatement.rows.map(r => [r.date, r.ref || '', r.description || (r.source || ''),
        r.amount_in ? r.amount_in.toLocaleString() : '', r.amount_out ? r.amount_out.toLocaleString() : '', r.balance.toLocaleString()]),
      foot: [['', '', 'Totals', poolStatement.total_in.toLocaleString(), poolStatement.total_out.toLocaleString(), poolStatement.closing_balance.toLocaleString()]],
      styles: { fontSize: 8 }, headStyles: { fillColor: [44, 62, 80] }, footStyles: { fillColor: [236, 240, 241], textColor: 20 },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    });
    doc.save(`Deposit_Statement_${m ? m.member_number : poolMemberId}.pdf`);
  };

  useEffect(() => {
    api.get('/api/members').then(res => setMembers(res.data)).catch(() => {});
    fetchDepositSlips();
    fetchRecoHistory();
  }, []);

  useEffect(() => {
    if (tab === 'landing') fetchLandingDeposits(landingStatusFilter);
    if (tab === 'allocation') fetchPoolMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, landingStatusFilter]);

  const fetchLandingDeposits = (status) => {
    axios.get(API + '/pending-deposits', { params: status ? { status } : {} })
      .then(res => setLandingDeposits(res.data))
      .catch(() => {});
  };

  const handleLandingCsvImport = async (e) => {
    e.preventDefault(); setError(''); setLandingCsvResult(null);
    if (!landingCsvFile) return setError('Please select a bank statement file');
    setLandingCsvLoading(true);
    const formData = new FormData();
    formData.append('csv', landingCsvFile);
    formData.append('period_year', landingCsvYear);
    formData.append('period_month', landingCsvMonth);
    try {
      const res = await axios.post(API + '/pending-deposits/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLandingCsvResult(res.data);
      const anomalyCount = res.data.anomalies?.length || 0;
      const dupCount = res.data.duplicates?.length || 0;
      const landedCount = res.data.auto_approved + res.data.needs_review;
      setSuccess(`Landed ${landedCount} deposit(s) — ${res.data.auto_approved} auto-approved, ${res.data.needs_review} need review.`
        + (dupCount ? ` ${dupCount} row(s) were already imported before and were skipped — see below.` : '')
        + (anomalyCount ? ` ${anomalyCount} row(s) could not be reconciled against the statement's balance and were excluded — see below.` : ''));
      setLandingCsvFile(null);
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Import failed'); }
    setLandingCsvLoading(false);
  };

  const handleSlipUpload = async (e) => {
    e.preventDefault(); setError('');
    if (!slipMember) return setError('Select a member first');
    if (!slipAmount) return setError('Amount required');
    if (!slipUploadFile) return setError('Please select a slip file');
    setSlipUploading(true);
    const formData = new FormData();
    formData.append('slip', slipUploadFile);
    formData.append('member_id', slipMember);
    formData.append('amount', slipAmount);
    formData.append('transaction_date', slipDate);
    formData.append('description', slipDescription);
    formData.append('source', slipSourceType);
    try {
      await axios.post(API + '/pending-deposits/upload-slip', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Slip landed — pending approval.');
      setSlipMember(''); setSlipAmount(''); setSlipDate(''); setSlipDescription(''); setSlipUploadFile(null);
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Upload failed'); }
    setSlipUploading(false);
  };

  const [changingMember, setChangingMember] = useState({});

  const reopenDeposit = async (d) => {
    const what = d.status === 'rejected' ? 'Reopen this rejected deposit' : 'Send this approved (not yet allocated) deposit back to Pending';
    if (!window.confirm(`${what}?\n\nUGX ${parseFloat(d.amount).toLocaleString()} — ${d.description || ''}\n\nYou can then assign the member, approve and allocate it again.`)) return;
    try {
      const res = await axios.post(API + '/pending-deposits/' + d.id + '/reopen');
      setSuccess(res.data.message || 'Deposit reopened.');
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Reopen failed'); }
  };

  const assignDeposit = async (id) => {
    const memberId = assignPicks[id];
    if (!memberId) return setError('Pick a member to assign first');
    try {
      await axios.patch(API + '/pending-deposits/' + id + '/assign', { member_id: memberId });
      setChangingMember(prev => ({ ...prev, [id]: false }));
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Assign failed'); }
  };

  // Resolves a member id to a display label using the already-loaded
  // members list — used to name the WhatsApp sender on an attribution
  // flag, since pending_deposits only stores reported_by_member_id.
  const memberName = (id) => {
    const m = members.find(x => String(x.id) === String(id));
    return m ? `${m.full_name} (${m.member_number})` : `member #${id}`;
  };

  // A deposit flagged mismatch/needs_review (depositorAttribution.js,
  // Sep 2026) must never be approved as casually as an ordinary one —
  // Finance sees exactly what disagreed before confirming either way.
  const approveDeposit = async (id) => {
    const d = landingDeposits.find(x => x.id === id);
    if (d && (d.attribution_status === 'mismatch' || d.attribution_status === 'needs_review')) {
      const detail = d.attribution_status === 'mismatch'
        ? `This deposit's slip resolved to ${d.full_name} (${d.member_number}), but it was sent from ${memberName(d.reported_by_member_id)}'s WhatsApp.`
        : `This deposit was attributed to ${d.full_name} (${d.member_number}) by a name match on the slip only — names aren't a reliable identifier.`;
      if (!window.confirm(detail + '\n\nApprove this attribution anyway?')) return;
    }
    try {
      await axios.post(API + '/pending-deposits/' + id + '/approve');
      setSuccess('Deposit approved.');
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Approve failed'); }
  };

  const rejectDeposit = async (id) => {
    const reason = window.prompt('Reason for rejecting this deposit (optional):') || '';
    try {
      await axios.post(API + '/pending-deposits/' + id + '/reject', { reason });
      setSuccess('Deposit rejected.');
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Reject failed'); }
  };

  const confidenceBadge = (confidence) => {
    const colors = { strongest: '#2ecc71', strong: '#27ae60', medium: '#f39c12', weak: '#e67e22' };
    if (!confidence) return <span style={{ color: '#95a5a6', fontSize: '11px' }}>no signal</span>;
    return (
      <span style={{ background: colors[confidence] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
        {confidence}
      </span>
    );
  };

  // match_confidence is written only by the bank-statement matcher
  // (paymentMatching.js, via /pending-deposits/import-csv). WhatsApp/
  // slip deposits never populate it — they're scored separately by
  // depositorAttribution.js's resolveDepositor(), which only ever
  // writes attribution_status. Without this, a slip that resolved on
  // a strong KAMS-code/phone match looked identical here to one where
  // nothing on the slip matched anyone at all ("no signal" either
  // way) — misleading, since "no signal" should mean exactly that.
  // attribution_status alone is enough to recover the tier:
  // 'confirmed'/'mismatch' only ever happen at the strong tier
  // (code or phone), 'needs_review' only at the weak tier (name),
  // 'unverified' is a genuine no-match fallback to the sender.
  const depositConfidence = (d) => {
    if (d.match_confidence) return d.match_confidence;
    if (d.attribution_status === 'confirmed' || d.attribution_status === 'mismatch') return 'strong';
    if (d.attribution_status === 'needs_review') return 'weak';
    return null;
  };

  const depositConfidenceReason = (d) => {
    if (d.match_confidence) return d.match_reason;
    if (d.attribution_status === 'confirmed') return "slip's depositor code/phone matched this member";
    if (d.attribution_status === 'mismatch') return "slip's depositor code/phone matched a different member — see flag above";
    if (d.attribution_status === 'needs_review') return "slip's depositor name matched this member — names aren't a reliable identifier";
    return null;
  };

  const sourceBadge = (source) => {
    const map = { bank_csv: ['#3498db', 'Bank CSV'], whatsapp_slip: ['#25D366', 'WhatsApp'], manual: ['#8e44ad', 'Manual'], member_portal: ['#e67e22', 'Member Portal'] };
    const [color, label] = map[source] || ['#95a5a6', source];
    return <span style={{ background: color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{label}</span>;
  };

  const previewAllocation = async (id) => {
    setAllocationPreview(null); setAllocateResult(null); setAllocationPreviewLoading(true); setError('');
    try {
      const res = await axios.get(API + '/pending-deposits/' + id + '/allocation-preview');
      setAllocationPreview(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load allocation preview'); }
    setAllocationPreviewLoading(false);
  };

  const executeAllocation = async (id) => {
    if (!window.confirm('Post this sweep for real? This credits treasury and clears obligations — it cannot be undone from here.')) return;
    setAllocating(true); setError('');
    try {
      const res = await axios.post(API + '/pending-deposits/' + id + '/allocate', { mode_of_payment: allocateMode });
      setAllocateResult(res.data);
      setSuccess('Deposit allocated and posted.');
      fetchLandingDeposits(landingStatusFilter);
    } catch (err) { setError(err.response?.data?.error || 'Allocation failed'); }
    setAllocating(false);
  };

  // A deposit's `status` column never moves past 'approved' once it's
  // actually posted — allocate() only sets treasury_transaction_id,
  // it doesn't change status (see payments.js). So "posted" here is
  // derived, not a real status value: approved + a treasury_transaction_id
  // means Confirm & Post already ran for this row. Shown as its own
  // badge so an already-posted row is never mistaken for one still
  // waiting on Preview Allocation.
  const depositStatusBadge = (d) => {
    const posted = d.status === 'approved' && d.treasury_transaction_id;
    const label = posted ? 'posted' : d.status;
    const colors = { pending: '#f39c12', approved: '#2ecc71', rejected: '#e74c3c', posted: '#16a085' };
    return (
      <span style={{ background: colors[label] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
        {label}
      </span>
    );
  };

  useEffect(() => {
    if (depositMember) {
      const member = members.find(m => String(m.id) === String(depositMember));
      if (member) setDepositRef('KAMS' + String(member.member_number).padStart(3, '0'));
    } else {
      setDepositRef('');
    }
  }, [depositMember, members]);

  const fetchDepositSlips = () => {
    axios.get(API + '/deposit-slips').then(res => setDepositSlips(res.data)).catch(() => {});
  };

  const fetchRecoHistory = () => {
    axios.get(API + '/reconcile/history').then(res => setRecoHistory(res.data)).catch(() => {});
  };

  const fetchCustomLines = async (memberId, y, m) => {
    if (!memberId) return;
    try {
      const res = await axios.get(API + '/custom-lines/' + memberId, { params: { year: y, month: m } });
      setCustomLines(res.data);
    } catch (_) { setCustomLines([]); }
  };

  const fetchObligation = async () => {
    if (!selectedMember) return setError('Select a member first');
    setError(''); setLoadingObligation(true);
    try {
      const res = await axios.get(API + '/obligation/' + selectedMember, { params: { year, month } });
      setObligation(res.data);
      await fetchCustomLines(selectedMember, year, month);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load obligation');
    }
    setLoadingObligation(false);
  };

  const saveAndNotify = async () => {
    if (!selectedMember) return setError('Select a member first');
    setError(''); setSuccess(''); setNotifySending(true);
    try {
      const res = await axios.post(API + '/slips/generate', { member_id: selectedMember, year, month });
      if (res.data.skipped) {
        setSuccess('Nothing to send — ' + (res.data.reason || 'no outstanding obligation') + '.');
      } else {
        setSuccess('Slip saved and sent to ' + (res.data.member?.full_name || 'member') + '.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save and notify');
    }
    setNotifySending(false);
  };

  const sendToAll = async () => {
    if (!window.confirm('Generate and send Payment Advice to every active member with an outstanding balance for ' + MONTHS[month-1] + ' ' + year + '?')) return;
    setError(''); setSuccess(''); setBulkSending(true); setBulkResult(null);
    try {
      const res = await axios.post(API + '/slips/generate-all', { year, month });
      setBulkResult(res.data);
      setSuccess(`Sent to ${res.data.generated} member(s), skipped ${res.data.skipped} (no balance due).`);
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk send failed');
    }
    setBulkSending(false);
  };

  const saveCustomLine = async () => {
    if (!selectedMember) return setError('Select a member first');
    if (!customForm.description.trim() || !customForm.amount) return setError('Description and amount required');
    setCustomSaving(true); setCustomMsg('');
    try {
      await axios.post(API + '/custom-lines', {
        member_id: selectedMember,
        period_year: year,
        period_month: month,
        description: customForm.description,
        amount: customForm.amount,
        due_date: customForm.due_date,
        notes: customForm.notes
      });
      setCustomMsg('Obligation added to member slip');
      setCustomForm({ description: '', amount: '', due_date: '', notes: '' });
      await fetchCustomLines(selectedMember, year, month);
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    setCustomSaving(false);
    setTimeout(() => setCustomMsg(''), 3000);
  };

  const deleteCustomLine = async (id) => {
    try {
      await axios.delete(API + '/custom-lines/' + id);
      await fetchCustomLines(selectedMember, year, month);
    } catch (err) { setError('Delete failed'); }
  };

  const generatePDF = () => {
    if (!obligation) return;
    const doc = new jsPDF();
    const m = obligation.member;
    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Kira Municipality, Uganda | Registered CBO', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('MEMBER PAYMENT ADVICE SLIP', 105, 40, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Member: ' + m.full_name + ' (' + m.member_number + ')', 15, 52);
    doc.text('Period: ' + MONTHS[obligation.month - 1] + ' ' + obligation.year, 15, 60);
    doc.text('Reference: ' + obligation.reference_number + ' or ' + String(m.member_number).padStart(3, '0'), 15, 68);
    doc.text('Generated: ' + new Date().toLocaleDateString('en-GB'), 15, 76);

    // Each line now carries its own due date (obligation.lines is
    // already built with due_date per item by adaptObligation() on the
    // backend) — shown here in the Due column so Finance can see, at a
    // glance, which obligation is closest to being due, same as the
    // member-facing advice.
    const lineRows = (obligation.lines || []).map(function(l) {
      const note = l.is_arrears
        ? (l.amount > 0 ? 'OVERDUE — MIFL ARREARS' : 'CLEAR')
        : (l.due_date ? 'Due: ' + l.due_date : 'No fixed due date');
      return [l.label, l.amount.toLocaleString(), note];
    });
    const customRows = customLines.map(function(l) {
      return [l.description, parseFloat(l.amount).toLocaleString(), l.due_date ? 'Due: ' + l.due_date.slice(0,10) : 'No fixed due date'];
    });
    const customTotal = customLines.reduce(function(s, l) { return s + parseFloat(l.amount); }, 0);
    const grandTotal = parseFloat(obligation.total_due) + customTotal;
    const allRows = lineRows.concat(customRows.length > 0 ? [['-- Additional Obligations --', '', '']].concat(customRows) : []);
    autoTable(doc, {
      startY: 85,
      head: [['Obligation', 'Amount (UGX)', 'Due']],
      body: allRows,
      foot: [['TOTAL TO DEPOSIT', grandTotal.toLocaleString(), '']],
      headStyles: { fillColor: [44, 62, 80] },
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', fontSize: 12 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 15 },
    });
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('BANKING DETAILS', 15, finalY);
    doc.setFont('helvetica', 'normal');
    doc.text('Centenary Bank — Account Name: Kigezi Association', 15, finalY + 8);
    doc.text('Account Number: 3100125358', 15, finalY + 16);
    doc.text('DFCU Bank — Account Name: Kigezi Association', 15, finalY + 26);
    doc.text('Account Number: 01521117543559', 15, finalY + 34);
    doc.text('Payment Reference: ' + obligation.reference_number + ' or ' + String(m.member_number).padStart(3, '0'), 15, finalY + 44);
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text('Kigezi Association — Official Document', 105, 285, { align: 'center' });
    doc.save('PaymentSlip-' + m.member_number + '-' + obligation.year + String(obligation.month).padStart(2,'0') + '.pdf');
  };

  const handleDepositUpload = async (e) => {
    e.preventDefault(); setError(''); setUploadSuccess('');
    if (!depositFile) return setError('Please select a file to upload');
    const formData = new FormData();
    formData.append('slip', depositFile);
    formData.append('member_id', depositMember);
    formData.append('reference_number', depositRef);
    formData.append('period_year', depositYear);
    formData.append('period_month', depositMonth);
    formData.append('amount_deposited', depositAmount);
    formData.append('deposit_date', depositDate);
    try {
      await axios.post(API + '/deposit-slips/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadSuccess('Deposit slip uploaded successfully');
      setDepositFile(null); setDepositAmount(''); setDepositDate('');
      fetchDepositSlips();
    } catch (err) { setError(err.response?.data?.error || 'Upload failed'); }
  };

  const handleReconcile = async (e) => {
    e.preventDefault(); setError(''); setRecoResult(null);
    if (!csvFile) return setError('Please select a CSV file');
    setRecoLoading(true);
    const formData = new FormData();
    formData.append('csv', csvFile);
    formData.append('period_year', recoYear);
    formData.append('period_month', recoMonth);
    try {
      const res = await axios.post(API + '/reconcile', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRecoResult(res.data);
      setSuccess('Reconciliation complete');
      fetchRecoHistory();
    } catch (err) { setError(err.response?.data?.error || 'Reconciliation failed'); }
    setRecoLoading(false);
  };

  const viewRunResults = async (runId) => {
    setSelectedRun(runId);
    try {
      const res = await axios.get(API + '/reconcile/' + runId + '/results');
      setRunResults(res.data);
    } catch (err) { setError('Failed to load run results'); }
  };

  const matchBadge = (status) => {
    const colors = { matched: '#2ecc71', shortfall: '#e74c3c', excess: '#f39c12', no_payment: '#95a5a6' };
    return (
      <span style={{ background: colors[status] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
        {(status || '').replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const customTotal = customLines.reduce(function(s, l) { return s + parseFloat(l.amount); }, 0);

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '5px' }}>Payments</h2>
      <p style={{ color: '#7f8c8d', marginBottom: '20px', fontSize: '13px' }}>
        Payment slips, deposit slip uploads, and CSV bank reconciliation
      </p>

      {error && (
        <div style={alertStyle('#e74c3c')}>
          {error}
          <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>x</span>
        </div>
      )}
      {success && (
        <div style={alertStyle('#2ecc71')}>
          {success}
          <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>x</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #ecf0f1', flexWrap: 'wrap' }}>
        {[
          { key: 'slips', label: 'Payment Slips' },
          { key: 'landing', label: 'Deposit Landing Pool' },
          { key: 'allocation', label: 'Allocation Pool' },
          { key: 'deposits', label: 'Deposit Slips' },
          { key: 'reconcile', label: 'CSV Reconciliation' },
          { key: 'history', label: 'History' }
        ].map(function(t) {
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px',
              background: tab === t.key ? '#2c3e50' : '#ecf0f1',
              color: tab === t.key ? 'white' : '#2c3e50',
              borderRadius: '4px 4px 0 0'
            }}>{t.label}</button>
          );
        })}
      </div>

      {tab === 'allocation' && (
        <div>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={sectionTitle}>Allocation Pool</h3>
              {!readOnly && poolMembers.length > 0 && (
                <button onClick={clearPool} disabled={poolBusy} style={{ ...smallBtn, background: '#c0392b', marginBottom: '16px' }}>Clear pool…</button>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '12px' }}>
              Approved deposits land here. A member's unallocated balance is allocated in one go across every obligation due
              (MIFL arrears → fines → savings → subscription → ROSCA → advances → loans due → next month early), and any excess goes to MIFL as an MCN credit.
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Select Member (members with an unallocated balance)</label>
              <select value={poolMembers.some(pm => String(pm.member_id) === String(poolMemberId)) ? poolMemberId : ''} onChange={e => loadPoolMember(e.target.value)} style={inputStyle}>
                <option value="">-- Select Member --</option>
                {[...poolMembers].sort((x, y) => (poolNumOf(x) - poolNumOf(y)) || String(x.member_number).localeCompare(String(y.member_number))).map(pm => (
                  <option key={pm.member_id} value={pm.member_id}>{pm.member_number} {pm.full_name} — UGX {pm.balance.toLocaleString()}</option>
                ))}
              </select>
            </div>
            {poolMembers.length === 0 && <div style={{ fontSize: '12px', color: '#27ae60', margin: '12px 0' }}>No unallocated balances — every approved deposit has been allocated.</div>}
            {(() => {
              const numOf = poolNumOf;
              const sorted = [...members].sort((x, y) => (numOf(x) - numOf(y)) || String(x.member_number).localeCompare(String(y.member_number)));
              const balById = {};
              poolMembers.forEach(pm => { balById[String(pm.member_id)] = pm; });
              const pages = Math.max(1, Math.ceil(sorted.length / POOL_PAGE_SIZE));
              const page = Math.min(poolPage, pages - 1);
              const slice = sorted.slice(page * POOL_PAGE_SIZE, (page + 1) * POOL_PAGE_SIZE);
              if (!sorted.length) return null;
              return (
                <div style={{ marginTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                      Members {slice[0] ? slice[0].member_number : ''} – {slice.length ? slice[slice.length - 1].member_number : ''} ({page * POOL_PAGE_SIZE + 1}–{page * POOL_PAGE_SIZE + slice.length} of {sorted.length})
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setPoolPage(Math.max(0, page - 1))} disabled={page === 0} style={{ ...smallBtn, background: page === 0 ? '#bdc3c7' : '#2c3e50' }}>← Previous {POOL_PAGE_SIZE}</button>
                      <button onClick={() => setPoolPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1} style={{ ...smallBtn, background: page >= pages - 1 ? '#bdc3c7' : '#2c3e50' }}>Next {POOL_PAGE_SIZE} →</button>
                    </div>
                  </div>
                  <table style={tableStyle}>
                    <thead><tr style={{ background: '#ecf0f1' }}>
                      <th style={thStyle}>No.</th><th style={thStyle}>Member</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Unallocated balance</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Deposits</th>
                    </tr></thead>
                    <tbody>
                      {slice.map(m => {
                        const pm = balById[String(m.id)];
                        const bal = pm ? pm.balance : 0;
                        const cnt = pm ? pm.deposit_count : 0;
                        const sel = String(m.id) === String(poolMemberId);
                        return (
                          <tr key={m.id} onClick={() => loadPoolMember(String(m.id))}
                            style={{ cursor: 'pointer', background: sel ? '#d5f5e3' : 'transparent', color: bal > 0 ? '#2c3e50' : '#95a5a6' }}>
                            <td style={tdStyle}>{m.member_number}</td>
                            <td style={{ ...tdStyle, fontWeight: bal > 0 ? '600' : 'normal' }}>{m.full_name}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: bal > 0 ? '600' : 'normal' }}>UGX {Number(bal).toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{cnt}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {poolPreview && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>Unallocated balance: UGX {poolPreview.balance.toLocaleString()} <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#7f8c8d' }}>({poolPreview.deposits.length} deposit{poolPreview.deposits.length === 1 ? '' : 's'})</span></h3>
              {poolPreview.deposits.length > 0 && (
                <table style={tableStyle}>
                  <thead><tr style={{ background: '#ecf0f1' }}><th style={thStyle}>Date</th><th style={thStyle}>Source</th><th style={thStyle}>Reference / Description</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th></tr></thead>
                  <tbody>{poolPreview.deposits.map(d => (
                    <tr key={d.id}><td style={tdStyle}>{String(d.transaction_date).slice(0, 10)}</td><td style={tdStyle}>{d.source}</td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{d.bank_reference || ''} {d.description || ''}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(d.amount).toLocaleString()}</td></tr>
                  ))}</tbody>
                </table>
              )}
              <h4 style={{ margin: '16px 0 8px', color: '#2c3e50', fontSize: '13px' }}>Allocation preview — obligations due now</h4>
              <table style={tableStyle}>
                <thead><tr style={{ background: '#ecf0f1' }}><th style={thStyle}>Obligation</th><th style={{ ...thStyle, textAlign: 'right' }}>Due</th><th style={{ ...thStyle, textAlign: 'right' }}>Will allocate</th><th style={{ ...thStyle, textAlign: 'right' }}>Still outstanding</th>{!readOnly && <th style={{ ...thStyle, textAlign: 'center' }}>{poolPriority.length > 0 ? <button onClick={() => setPriorityAndReload([])} style={{ ...smallBtn, background: '#7f8c8d' }}>Reset order</button> : 'Priority'}</th>}</tr></thead>
                <tbody>
                  {poolPreview.lines.length === 0 && <tr><td colSpan="5" style={{ ...tdStyle, color: '#95a5a6' }}>No obligations due.</td></tr>}
                  {poolPreview.lines.map(l => (
                    <tr key={l.key} style={{ background: poolPriority.includes(l.key) ? '#fef9e7' : 'transparent' }}>
                      <td style={tdStyle}>{poolPriority.includes(l.key) ? <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#b9770e', marginRight: '6px' }}>PRIORITY {poolPriority.indexOf(l.key) + 1}</span> : null}{l.label}{l.upcoming ? <span style={{ fontSize: '10px', color: '#2980b9' }}> (early)</span> : null}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{l.amount.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#1e8449', fontWeight: 'bold' }}>{l.applied > 0 ? l.applied.toLocaleString() : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: l.amount - l.applied > 0 && !(l.optional && !l.applied) ? '#c0392b' : '#95a5a6' }}>{l.amount - l.applied > 0 && !(l.optional && !l.applied) ? (l.amount - l.applied).toLocaleString() : '—'}</td>
                      {!readOnly && (
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {poolPriority.includes(l.key)
                            ? <button onClick={() => setPriorityAndReload(poolPriority.filter(k => k !== l.key))} style={{ ...smallBtn, background: '#b9770e' }}>Undo</button>
                            : <button onClick={() => setPriorityAndReload([...poolPriority, l.key])} style={{ ...smallBtn, background: '#2980b9' }}>Allocate first</button>}
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr style={{ background: '#fdfefe', fontWeight: 'bold' }}>
                    <td style={tdStyle}>Excess → MIFL credit (MCN)</td><td style={tdStyle}></td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#2980b9' }}>{poolPreview.excess > 0 ? poolPreview.excess.toLocaleString() : '—'}</td><td style={tdStyle}></td>{!readOnly && <td style={tdStyle}></td>}
                  </tr>
                </tbody>
              </table>
              {!readOnly && poolPreview.balance > 0 && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginTop: '14px', flexWrap: 'wrap' }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Account the deposits landed in *</label>
                    <select value={poolMode} onChange={e => setPoolMode(e.target.value)} style={inputStyle}>
                      <option value="centenary">Centenary Bank</option>
                      <option value="dfcu">DFCU Bank</option>
                      <option value="cash">Cash</option>
                    </select>
                  </div>
                  <button onClick={allocatePool} disabled={poolBusy} style={{ ...btnStyle, background: '#16a085' }}>
                    {poolBusy ? 'Allocating…' : `✓ Allocate UGX ${poolPreview.balance.toLocaleString()}`}
                  </button>
                </div>
              )}
            </div>
          )}

          {poolStatement && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ ...sectionTitle, margin: 0 }}>Deposit Statement</h3>
                {poolStatement.rows.length > 0 && <button onClick={exportStatementPDF} style={{ ...smallBtn, background: '#1a5276' }}>📄 Export PDF</button>}
              </div>
              <table style={{ ...tableStyle, marginTop: '10px' }}>
                <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Date</th><th style={thStyle}>Ref</th><th style={thStyle}>Details</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>In</th><th style={{ ...thStyle, textAlign: 'right' }}>Out</th><th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                </tr></thead>
                <tbody>
                  {poolStatement.rows.length === 0 && <tr><td colSpan="6" style={{ ...tdStyle, color: '#95a5a6', textAlign: 'center' }}>No entries yet (statement starts from Allocation Pool go-live).</td></tr>}
                  {poolStatement.rows.map((r, i) => (
                    <tr key={i} style={{ background: r.kind === 'deposit' ? '#eafaf1' : (i % 2 ? 'white' : '#f9f9f9') }}>
                      <td style={tdStyle}>{r.date}</td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{r.ref}</td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{r.kind === 'deposit' ? `Deposit (${r.source}) ${r.description}` : r.description}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#1e8449' }}>{r.amount_in ? r.amount_in.toLocaleString() : ''}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#c0392b' }}>{r.amount_out ? r.amount_out.toLocaleString() : ''}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{r.balance.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                {poolStatement.rows.length > 0 && (
                  <tfoot><tr style={{ background: '#ecf0f1', fontWeight: 'bold' }}>
                    <td style={tdStyle} colSpan="3">Totals</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{poolStatement.total_in.toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{poolStatement.total_out.toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{poolStatement.closing_balance.toLocaleString()}</td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'slips' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Generate Payment Slip</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Member</label>
                <select value={selectedMember} onChange={e => setSelectedMember(e.target.value)} style={inputStyle}>
                  <option value="">-- Select Member --</option>
                  {members.map(m => (
                    <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>
                  ))}
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Year</label>
                <input type="number" value={year} onChange={e => setYear(e.target.value)} style={{ ...inputStyle, width: '90px' }} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Month</label>
                <select value={month} onChange={e => setMonth(e.target.value)} style={inputStyle}>
                  {MONTHS.map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
              </div>
              <button onClick={fetchObligation} style={btnStyle} disabled={loadingObligation}>
                {loadingObligation ? 'Calculating...' : 'Calculate'}
              </button>
              {!readOnly && <button onClick={sendToAll} style={{ ...btnStyle, background: '#16a085' }} disabled={bulkSending}>
                {bulkSending ? 'Sending...' : '📢 Send to All Active Members'}
              </button>}
            </div>
            {bulkResult && bulkResult.errors && bulkResult.errors.length > 0 && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#e74c3c' }}>
                {bulkResult.errors.length} member(s) failed — check server logs for details.
              </div>
            )}
          </div>

          {obligation && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#2c3e50' }}>{obligation.member.full_name}</h3>
                  <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '12px' }}>
                    Ref: <strong style={{ color: '#e74c3c' }}>{obligation.reference_number}</strong>
                    {' | '}Period: {MONTHS[obligation.month - 1]} {obligation.year}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={generatePDF} style={{ ...btnStyle, background: '#e74c3c' }}>Download PDF</button>
                  {!readOnly && <button onClick={saveAndNotify} style={{ ...btnStyle, background: '#27ae60' }} disabled={notifySending}>
                    {notifySending ? 'Sending...' : '📲 Save & Notify Member'}
                  </button>}
                </div>
              </div>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Obligation</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount (UGX)</th>
                    <th style={thStyle}>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(obligation.lines || []).length === 0 && (
                    <tr><td colSpan="3" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No outstanding obligations this period</td></tr>
                  )}
                  {(obligation.lines || []).map(function(l, i) {
                    const days = daysUntil(l.due_date);
                    const urgency = urgencyInfo(days, l.is_arrears, l.amount);
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={tdStyle}>{l.label}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(l.amount).toLocaleString()}</td>
                        <td style={tdStyle}>
                          {l.due_date ? (
                            <span>{l.due_date} <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '600', color: urgency.color }}>({urgency.label})</span></span>
                          ) : (
                            <span style={{ fontSize: '11px', color: urgency.color, fontWeight: '600' }}>{urgency.label}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {customLines.map(function(l, i) {
                    const days = daysUntil(l.due_date ? l.due_date.slice(0, 10) : null);
                    const urgency = urgencyInfo(days, false, 0);
                    return (
                      <tr key={'c' + l.id} style={{ background: '#f0f4ff' }}>
                        <td style={tdStyle}>
                          <span style={{ color: '#8e44ad', marginRight: '6px', fontSize: '11px' }}>+</span>
                          {l.description}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#8e44ad' }}>{parseFloat(l.amount).toLocaleString()}</td>
                        <td style={tdStyle}>
                          {l.due_date ? (
                            <span>{l.due_date.slice(0,10)} <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '600', color: urgency.color }}>({urgency.label})</span></span>
                          ) : <span style={{ fontSize: '11px', color: '#95a5a6' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#2c3e50', color: 'white', fontWeight: 'bold' }}>
                    <td style={tdStyle}>TOTAL TO DEPOSIT</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      UGX {(parseFloat(obligation.total_due) + customTotal).toLocaleString()}
                    </td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '11px', color: '#95a5a6', marginTop: '10px' }}>
                "Download PDF" saves a copy for your own records only. "Save & Notify Member" records this slip and sends the member their breakdown, deposit reference, and PDF link via WhatsApp.
              </p>
            </div>
          )}

          {obligation && (
            <div style={{ ...cardStyle, marginTop: '16px', borderTop: '3px solid #8e44ad' }}>
              <h3 style={{ ...sectionTitle, color: '#8e44ad' }}>Add Custom Obligation to Member Slip</h3>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '14px' }}>
                Added items appear on {obligation.member.full_name}'s portal slip for {MONTHS[month-1]} {year}. The member is notified automatically when you add one.
              </p>
              {customMsg && (
                <div style={{ background: '#f0e6ff', border: '1px solid #8e44ad', color: '#6c3483', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
                  {customMsg}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'flex-end' }}>
                <div style={{ ...fieldStyle, flex: 2, minWidth: '200px' }}>
                  <label style={labelStyle}>Description *</label>
                  <input style={inputStyle} value={customForm.description}
                    onChange={e => setCustomForm({ ...customForm, description: e.target.value })}
                    placeholder="e.g. Special Levy May 2026" />
                </div>
                <div style={{ ...fieldStyle, minWidth: '130px' }}>
                  <label style={labelStyle}>Amount (UGX) *</label>
                  <input type="number" style={inputStyle} value={customForm.amount}
                    onChange={e => setCustomForm({ ...customForm, amount: e.target.value })}
                    placeholder="50000" />
                </div>
                <div style={{ ...fieldStyle, minWidth: '140px' }}>
                  <label style={labelStyle}>Due Date</label>
                  <input type="date" style={inputStyle} value={customForm.due_date}
                    onChange={e => setCustomForm({ ...customForm, due_date: e.target.value })} />
                </div>
                <div style={{ ...fieldStyle, flex: 1, minWidth: '150px' }}>
                  <label style={labelStyle}>Note (optional)</label>
                  <input style={inputStyle} value={customForm.notes}
                    onChange={e => setCustomForm({ ...customForm, notes: e.target.value })}
                    placeholder="Additional note" />
                </div>
                {!readOnly && <button onClick={saveCustomLine} style={{ ...btnStyle, background: '#8e44ad' }} disabled={customSaving}>
                  {customSaving ? 'Saving...' : '+ Add'}
                </button>}
              </div>
              {customLines.length > 0 && (
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: '#8e44ad', color: 'white' }}>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Due Date</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount (UGX)</th>
                      <th style={thStyle}>Note</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customLines.map(function(l, i) {
                      return (
                        <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f4ff' : 'white' }}>
                          <td style={tdStyle}>{l.description}</td>
                          <td style={tdStyle}>{l.due_date ? l.due_date.slice(0,10) : '—'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(l.amount).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontSize: '11px', color: '#7f8c8d' }}>{l.notes || '—'}</td>
                          <td style={tdStyle}>
                            {!readOnly && <button onClick={() => deleteCustomLine(l.id)}
                              style={{ background: '#fde8e8', color: '#c0392b', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                              Remove
                            </button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'landing' && (
        <div>
          <div style={{ ...cardStyle, borderTop: '3px solid #16a085' }}>
            <h3 style={{ ...sectionTitle, color: '#16a085' }}>Import Bank Statement</h3>
            <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '14px' }}>
              Every credit row lands here first, attributed to whichever member the matcher finds. Nothing is auto-approved — every row waits for you below and moves to the Allocation Pool only when you approve it. This only lands deposits — it does not touch any member's obligation or the treasury yet.
            </p>
            <form onSubmit={handleLandingCsvImport}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Year *</label>
                  <input type="number" value={landingCsvYear} onChange={e => setLandingCsvYear(e.target.value)} style={{ ...inputStyle, width: '90px' }} required />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Month *</label>
                  <select value={landingCsvMonth} onChange={e => setLandingCsvMonth(e.target.value)} style={inputStyle} required>
                    {MONTHS.map((m, i) => (<option key={i+1} value={i+1}>{m}</option>))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Bank Statement (PDF or CSV) *</label>
                  <input type="file" accept=".pdf,.csv" onChange={e => setLandingCsvFile(e.target.files[0])} style={inputStyle} required />
                </div>
                {!readOnly && <button type="submit" style={{ ...btnStyle, background: '#16a085' }} disabled={landingCsvLoading}>
                  {landingCsvLoading ? 'Importing...' : 'Import'}
                </button>}
              </div>
            </form>
            {landingCsvResult && (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                {[
                  ['Credit Rows', landingCsvResult.credit_rows, '#3498db'],
                  ['Auto-Approved', landingCsvResult.auto_approved, '#2ecc71'],
                  ['Needs Review', landingCsvResult.needs_review, '#f39c12'],
                ].map(function(c, i) {
                  return (
                    <div key={i} style={{ background: c[2], color: 'white', padding: '12px 20px', borderRadius: '6px', minWidth: '100px', textAlign: 'center' }}>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{c[1]}</div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>{c[0]}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {landingCsvResult && landingCsvResult.anomalies && landingCsvResult.anomalies.length > 0 && (
              <div style={{ marginTop: '16px', background: '#fdf2e9', border: '1px solid #e67e22', borderRadius: '6px', padding: '12px 16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d35400', marginBottom: '8px' }}>
                  {landingCsvResult.anomalies.length} row(s) excluded — statement balance didn't reconcile
                </div>
                <p style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px' }}>
                  These rows were NOT landed as deposits and were NOT matched to anyone. The printed debit/credit for each doesn't match how the statement's own running balance actually moved — check the original PDF before deciding what, if anything, to do with them.
                </p>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#d35400' }}>
                      <th style={{ padding: '4px 8px' }}>Date</th>
                      <th style={{ padding: '4px 8px' }}>Description</th>
                      <th style={{ padding: '4px 8px' }}>Debit</th>
                      <th style={{ padding: '4px 8px' }}>Credit</th>
                      <th style={{ padding: '4px 8px' }}>Printed Balance</th>
                      <th style={{ padding: '4px 8px' }}>Expected Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {landingCsvResult.anomalies.map((a, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f5cba7' }}>
                        <td style={{ padding: '4px 8px' }}>{a.date}</td>
                        <td style={{ padding: '4px 8px' }}>{a.description}</td>
                        <td style={{ padding: '4px 8px' }}>{a.debit ? a.debit.toLocaleString() : '-'}</td>
                        <td style={{ padding: '4px 8px' }}>{a.credit ? a.credit.toLocaleString() : '-'}</td>
                        <td style={{ padding: '4px 8px' }}>{a.balance.toLocaleString()}</td>
                        <td style={{ padding: '4px 8px' }}>{a.expected_balance != null ? a.expected_balance.toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {landingCsvResult && landingCsvResult.duplicates && landingCsvResult.duplicates.length > 0 && (
              <div style={{ marginTop: '16px', background: '#eaf2f8', border: '1px solid #3498db', borderRadius: '6px', padding: '12px 16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#2874a6', marginBottom: '8px' }}>
                  {landingCsvResult.duplicates.length} row(s) skipped — already imported previously
                </div>
                <p style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px' }}>
                  These exact transactions (same date, description, and amount) already exist in the Landing Pool from an earlier import — not landed again, to avoid double-counting.
                </p>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#2874a6' }}>
                      <th style={{ padding: '4px 8px' }}>Date</th>
                      <th style={{ padding: '4px 8px' }}>Description</th>
                      <th style={{ padding: '4px 8px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {landingCsvResult.duplicates.map((d, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #aed6f1' }}>
                        <td style={{ padding: '4px 8px' }}>{d.date}</td>
                        <td style={{ padding: '4px 8px' }}>{d.description}</td>
                        <td style={{ padding: '4px 8px' }}>{d.credit ? d.credit.toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ ...cardStyle, marginTop: '16px', borderTop: '3px solid #8e44ad' }}>
            <h3 style={{ ...sectionTitle, color: '#8e44ad' }}>Upload a Slip</h3>
            <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '14px' }}>
              Manual staff upload, or a WhatsApp-forwarded slip. Always lands attributed to the member below and always waits for your approval — never auto-approved, regardless of how obvious the match looks.
            </p>
            <form onSubmit={handleSlipUpload}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Member *</label>
                  <select value={slipMember} onChange={e => setSlipMember(e.target.value)} style={inputStyle} required>
                    <option value="">-- Select --</option>
                    {members.map(m => (<option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Amount (UGX) *</label>
                  <input type="number" value={slipAmount} onChange={e => setSlipAmount(e.target.value)} style={inputStyle} required />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Deposit Date</label>
                  <input type="date" value={slipDate} onChange={e => setSlipDate(e.target.value)} style={inputStyle} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Source</label>
                  <select value={slipSourceType} onChange={e => setSlipSourceType(e.target.value)} style={inputStyle}>
                    <option value="manual">Manual (staff upload)</option>
                    <option value="whatsapp_slip">WhatsApp</option>
                  </select>
                </div>
                <div style={{ ...fieldStyle, flex: 1, minWidth: '160px' }}>
                  <label style={labelStyle}>Note</label>
                  <input value={slipDescription} onChange={e => setSlipDescription(e.target.value)} style={inputStyle} placeholder="Optional" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Slip File *</label>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setSlipUploadFile(e.target.files[0])} style={inputStyle} required />
                </div>
                {!readOnly && <button type="submit" style={{ ...btnStyle, background: '#8e44ad' }} disabled={slipUploading}>
                  {slipUploading ? 'Uploading...' : 'Upload'}
                </button>}
              </div>
            </form>
          </div>

          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ ...sectionTitle, margin: 0 }}>Landing Pool</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['pending', 'approved', 'rejected', ''].map(function(s) {
                  return (
                    <button key={s || 'all'} onClick={() => setLandingStatusFilter(s)} style={{
                      padding: '5px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                      background: landingStatusFilter === s ? '#2c3e50' : '#ecf0f1',
                      color: landingStatusFilter === s ? 'white' : '#2c3e50',
                    }}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}</button>
                  );
                })}
              </div>
            </div>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Member</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Confidence</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {landingDeposits.length === 0 && (
                  <tr><td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>Nothing here</td></tr>
                )}
                {landingDeposits.map(function(d, i) {
                  return (
                    <tr key={d.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{sourceBadge(d.source)}</td>
                      <td style={tdStyle}>
                        {d.member_id && !changingMember[d.id] ? (
                          <span>
                            <strong>{d.full_name}</strong><br /><span style={{ fontSize: '11px', color: '#7f8c8d' }}>{d.member_number}</span>
                            {!readOnly && d.status === 'pending' && (
                              <button onClick={() => { setAssignPicks({ ...assignPicks, [d.id]: String(d.member_id) }); setChangingMember({ ...changingMember, [d.id]: true }); }}
                                style={{ ...smallBtn, background: '#7f8c8d', marginLeft: '6px', padding: '2px 6px', fontSize: '10px' }}>Change</button>
                            )}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <select style={{ ...inputStyle, padding: '4px', fontSize: '11px', minWidth: '140px' }}
                              value={assignPicks[d.id] || ''}
                              onChange={e => setAssignPicks({ ...assignPicks, [d.id]: e.target.value })}>
                              <option value="">-- Assign --</option>
                              {members.map(m => (<option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>))}
                            </select>
                            {!readOnly && d.status === 'pending' && (
                              <button onClick={() => assignDeposit(d.id)} style={smallBtn}>Set</button>
                            )}
                            {changingMember[d.id] && (
                              <button onClick={() => setChangingMember({ ...changingMember, [d.id]: false })} style={{ ...smallBtn, background: '#95a5a6' }}>✕</button>
                            )}
                          </div>
                        )}
                        {d.candidates && d.candidates.length > 0 && (
                          <div style={{ fontSize: '10px', color: '#95a5a6', marginTop: '3px' }}>
                            candidates: {d.candidates.map(c => `${c.member.full_name} (${c.score})`).join(', ')}
                          </div>
                        )}
                        {d.attribution_status === 'mismatch' && (
                          <div style={{ fontSize: '10px', color: '#c0392b', marginTop: '3px', fontWeight: 'bold' }}>
                            ⚠ Mismatch — sent from {memberName(d.reported_by_member_id)}'s WhatsApp
                          </div>
                        )}
                        {d.attribution_status === 'needs_review' && (
                          <div style={{ fontSize: '10px', color: '#e67e22', marginTop: '3px', fontWeight: 'bold' }}>
                            ⚠ Name match only — please confirm
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 'bold' }}>{parseFloat(d.amount).toLocaleString()}</td>
                      <td style={tdStyle}>{d.transaction_date ? String(d.transaction_date).slice(0,10) : '—'}</td>
                      <td style={{ ...tdStyle, fontSize: '11px', maxWidth: '220px' }}>{d.description || '—'}</td>
                      <td style={tdStyle}>
                        {confidenceBadge(depositConfidence(d))}
                        {depositConfidenceReason(d) && <div style={{ fontSize: '10px', color: '#95a5a6', marginTop: '3px' }}>{depositConfidenceReason(d)}</div>}
                      </td>
                      <td style={tdStyle}>{depositStatusBadge(d)}</td>
                      <td style={tdStyle}>
                        {!readOnly && d.status === 'pending' && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => approveDeposit(d.id)} disabled={!d.member_id}
                              style={{ ...smallBtn, background: d.member_id ? '#27ae60' : '#bdc3c7', cursor: d.member_id ? 'pointer' : 'not-allowed' }}>Approve</button>
                            <button onClick={() => rejectDeposit(d.id)} style={{ ...smallBtn, background: '#c0392b' }}>Reject</button>
                          </div>
                        )}
                        {d.status === 'approved' && (
                          <div>
                            {d.treasury_transaction_id && !d.allocation_batch_id && (
                              <>
                                <button onClick={() => previewAllocation(d.id)} style={{ ...smallBtn, background: '#16a085' }}>View Allocation</button>
                                <div style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '3px' }}>posted — treasury txn #{d.treasury_transaction_id}</div>
                              </>
                            )}
                            {d.allocation_batch_id && (
                              <button onClick={() => goToAllocationPool(d.member_id)} style={{ ...smallBtn, background: '#7f8c8d' }}>Allocated — view statement</button>
                            )}
                            {!d.treasury_transaction_id && (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                <button onClick={() => goToAllocationPool(d.member_id)} style={{ ...smallBtn, background: '#16a085' }}>→ Allocation Pool</button>
                                {!readOnly && <button onClick={() => reopenDeposit(d)} style={{ ...smallBtn, background: '#e67e22' }}>↺ Send back</button>}
                              </div>
                            )}
                          </div>
                        )}
                        {d.status === 'rejected' && (
                          <div>
                            {!readOnly && <button onClick={() => reopenDeposit(d)} style={{ ...smallBtn, background: '#2980b9' }}>↺ Reopen</button>}
                            {d.rejected_reason && <div style={{ fontSize: '10px', color: '#95a5a6', marginTop: '3px' }}>rejected: {d.rejected_reason}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {allocationPreviewLoading && (
            <div style={{ ...cardStyle, marginTop: '16px', textAlign: 'center', color: '#7f8c8d' }}>Loading preview...</div>
          )}
          {allocationPreview && (
            <div style={{ ...cardStyle, marginTop: '16px', borderTop: '3px solid #16a085' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ ...sectionTitle, margin: 0, color: '#16a085' }}>
                  Allocation Preview — {allocationPreview.deposit.full_name} ({allocationPreview.deposit.member_number})
                </h3>
                <span onClick={() => setAllocationPreview(null)} style={{ cursor: 'pointer', color: '#95a5a6', fontSize: '13px' }}>Close</span>
              </div>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '14px' }}>
                UGX {parseFloat(allocationPreview.deposit.amount).toLocaleString()} swept in bylaw order. This is a preview only — nothing has been posted yet. Review the sweep below, then use Confirm & Post to actually post it.
              </p>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#16a085', color: 'white' }}>
                    <th style={thStyle}>Obligation</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Requested</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Applied</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allocationPreview.plan.steps.map(function(s, i) {
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={tdStyle}>
                          {s.label}
                          {!s.executable && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#e67e22' }}>(not yet automated)</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{s.requested.toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: s.applied > 0 ? '#16a085' : '#bdc3c7' }}>{s.applied.toLocaleString()}</td>
                        <td style={tdStyle}>
                          {s.applied >= s.requested
                            ? <span style={{ color: '#27ae60', fontSize: '11px' }}>cleared</span>
                            : s.applied > 0
                              ? <span style={{ color: '#f39c12', fontSize: '11px' }}>partial</span>
                              : <span style={{ color: '#95a5a6', fontSize: '11px' }}>unfunded</span>}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#2c3e50', color: 'white', fontWeight: 'bold' }}>
                    <td style={tdStyle}>TOTAL APPLIED</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{allocationPreview.plan.total_requested.toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{allocationPreview.plan.total_applied.toLocaleString()}</td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '12px', marginTop: '14px' }}>
                {allocationPreview.plan.shortfall > 0 && (
                  <div style={{ fontSize: '12px', color: '#e74c3c' }}>Shortfall: UGX {allocationPreview.plan.shortfall.toLocaleString()} — obligations this deposit doesn't cover</div>
                )}
                {allocationPreview.plan.excess > 0 && (
                  <div style={{ fontSize: '12px', color: '#f39c12' }}>Excess: UGX {allocationPreview.plan.excess.toLocaleString()} — left over after every obligation shown above</div>
                )}
                {allocationPreview.plan.shortfall === 0 && allocationPreview.plan.excess === 0 && (
                  <div style={{ fontSize: '12px', color: '#27ae60' }}>Exact match — covers every obligation shown above with nothing left over</div>
                )}
              </div>

              {!readOnly && !allocateResult && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Account the deposit landed in</label>
                    <select value={allocateMode} onChange={e => setAllocateMode(e.target.value)} style={inputStyle}>
                      <option value="centenary">Centenary Bank</option>
                      <option value="dfcu">DFCU Bank</option>
                      <option value="cash">Cash in Hand</option>
                    </select>
                  </div>
                  <button onClick={() => executeAllocation(allocationPreview.deposit.id)} style={{ ...btnStyle, background: '#27ae60' }} disabled={allocating}>
                    {allocating ? 'Posting...' : 'Confirm & Post'}
                  </button>
                </div>
              )}

              {allocateResult && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                  <div style={{ fontSize: '12px', color: '#27ae60', fontWeight: 'bold', marginBottom: '8px' }}>
                    Posted — treasury transaction #{allocateResult.treasury_transaction_id}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px' }}>
                    {allocateResult.posted.map(function(p, i) {
                      return <li key={i}>{p.label || p.bucket}: UGX {p.applied.toLocaleString()}{p.document_number ? ` (${p.document_number})` : ''}</li>;
                    })}
                    {allocateResult.mcn_issued && (
                      <li>Unutilised credit ({allocateResult.mcn_issued.document_number}): UGX {allocateResult.mcn_issued.amount.toLocaleString()}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'deposits' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Upload Deposit Slip</h3>
            <form onSubmit={handleDepositUpload}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Member *</label>
                  <select value={depositMember} onChange={e => setDepositMember(e.target.value)} style={inputStyle} required>
                    <option value="">-- Select --</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>
                    ))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Year *</label>
                  <input type="number" value={depositYear} onChange={e => setDepositYear(e.target.value)} style={{ ...inputStyle, width: '90px' }} required />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Month *</label>
                  <select value={depositMonth} onChange={e => setDepositMonth(e.target.value)} style={inputStyle} required>
                    {MONTHS.map((m, i) => (
                      <option key={i+1} value={i+1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Reference No.</label>
                  <input value={depositRef} onChange={e => setDepositRef(e.target.value)}
                    style={{ ...inputStyle, background: depositRef ? '#f0fff4' : undefined }}
                    placeholder="Auto-filled on member select" readOnly={!!depositMember} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Amount Deposited (UGX)</label>
                  <input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} style={inputStyle} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Deposit Date</label>
                  <input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)} style={inputStyle} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Bank Slip (JPG/PNG/PDF) *</label>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setDepositFile(e.target.files[0])} style={inputStyle} required />
                </div>
              </div>
              {!readOnly && <button type="submit" style={{ ...btnStyle, marginTop: '16px' }}>Upload Slip</button>}
            </form>
            {uploadSuccess && (
              <div style={{ ...alertStyle('#2ecc71'), marginTop: '12px' }}>{uploadSuccess}</div>
            )}
          </div>
          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <h3 style={sectionTitle}>Uploaded Slips</h3>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Member</th>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Reference</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>File</th>
                </tr>
              </thead>
              <tbody>
                {depositSlips.length === 0 && (
                  <tr><td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No slips uploaded yet</td></tr>
                )}
                {depositSlips.map(function(s, i) {
                  return (
                    <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>
                        <strong>{s.full_name}</strong><br />
                        <span style={{ fontSize: '11px', color: '#7f8c8d' }}>{s.member_number}</span>
                      </td>
                      <td style={tdStyle}>{MONTHS[s.period_month - 1]} {s.period_year}</td>
                      <td style={tdStyle}><code style={{ fontSize: '11px' }}>{s.reference_number || '—'}</code></td>
                      <td style={tdStyle}>{s.amount_deposited ? 'UGX ' + parseFloat(s.amount_deposited).toLocaleString() : '—'}</td>
                      <td style={tdStyle}>{s.deposit_date ? s.deposit_date.slice(0,10) : '—'}</td>
                      <td style={tdStyle}>
                        <span style={{ background: s.status === 'reconciled' ? '#2ecc71' : '#f39c12', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                          {s.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {s.slip_path
                          ? <a href={`${FILE_BASE}/api/payments/deposit-slips/file/${s.slip_path}`} target="_blank" rel="noreferrer" style={{ color: '#3498db', fontSize: '12px' }}>View</a>
                          : <span style={{ color: '#95a5a6', fontSize: '12px' }}>No file</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'reconcile' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Upload Bank Statement</h3>
            <form onSubmit={handleReconcile}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Year *</label>
                  <input type="number" value={recoYear} onChange={e => setRecoYear(e.target.value)} style={{ ...inputStyle, width: '90px' }} required />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Month *</label>
                  <select value={recoMonth} onChange={e => setRecoMonth(e.target.value)} style={inputStyle} required>
                    {MONTHS.map((m, i) => (
                      <option key={i+1} value={i+1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Bank Statement (PDF or CSV) *</label>
                  <input type="file" accept=".pdf,.csv" onChange={e => setCsvFile(e.target.files[0])} style={inputStyle} required />
                </div>
                {!readOnly && <button type="submit" style={{ ...btnStyle, background: '#8e44ad' }} disabled={recoLoading}>
                  {recoLoading ? 'Processing...' : 'Run Reconciliation'}
                </button>}
              </div>
            </form>
            <p style={{ fontSize: '11px', color: '#95a5a6', marginTop: '10px' }}>
              Members are automatically notified of the outcome — a debit note (shortfall), credit note (overpayment), or confirmation (exact match). Members with no payment on record are not auto-notified; review them below and follow up manually.
            </p>
          </div>
          {recoResult && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>Results — {MONTHS[recoResult.period_month - 1]} {recoResult.period_year}</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {[
                  ['Bank Transactions', recoResult.total_transactions, '#3498db'],
                  ['Matched', recoResult.matched, '#2ecc71'],
                  ['Unmatched', recoResult.unmatched, '#95a5a6'],
                  ['Shortfalls', recoResult.shortfall_count, '#e74c3c'],
                  ['Excess', recoResult.excess_count, '#f39c12'],
                ].map(function(c, i) {
                  return (
                    <div key={i} style={{ background: c[2], color: 'white', padding: '12px 20px', borderRadius: '6px', minWidth: '100px', textAlign: 'center' }}>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{c[1]}</div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>{c[0]}</div>
                    </div>
                  );
                })}
              </div>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Member</th>
                    <th style={thStyle}>Reference</th>
                    <th style={thStyle}>Obligation</th>
                    <th style={thStyle}>Bank CSV</th>
                    <th style={thStyle}>Shortfall</th>
                    <th style={thStyle}>Excess</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recoResult.results.map(function(r, i) {
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={tdStyle}>
                          <strong>{r.full_name}</strong><br />
                          <span style={{ fontSize: '11px', color: '#7f8c8d' }}>{r.member_number}</span>
                        </td>
                        <td style={tdStyle}><code style={{ fontSize: '10px' }}>{r.reference_number}</code></td>
                        <td style={tdStyle}>{parseFloat(r.obligation_amount).toLocaleString()}</td>
                        <td style={tdStyle}>{r.bank_csv_amount ? parseFloat(r.bank_csv_amount).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, color: '#e74c3c' }}>{r.shortfall > 0 ? parseFloat(r.shortfall).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, color: '#f39c12' }}>{r.excess > 0 ? parseFloat(r.excess).toLocaleString() : '—'}</td>
                        <td style={tdStyle}>{matchBadge(r.match_status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Reconciliation History</h3>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Run ID</th>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Run Date</th>
                  <th style={thStyle}>CSV File</th>
                  <th style={thStyle}>Transactions</th>
                  <th style={thStyle}>Matched</th>
                  <th style={thStyle}>Shortfalls</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recoHistory.length === 0 && (
                  <tr><td colSpan="9" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No reconciliation runs yet</td></tr>
                )}
                {recoHistory.map(function(r, i) {
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>#{r.id}</td>
                      <td style={tdStyle}>{MONTHS[r.period_month - 1]} {r.period_year}</td>
                      <td style={tdStyle}>{new Date(r.run_date).toLocaleString('en-GB')}</td>
                      <td style={tdStyle}>{r.csv_filename}</td>
                      <td style={tdStyle}>{r.total_transactions}</td>
                      <td style={tdStyle}>{r.matched}</td>
                      <td style={{ ...tdStyle, color: '#e74c3c' }}>{r.shortfall_count}</td>
                      <td style={tdStyle}>
                        <span style={{ background: '#2ecc71', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{r.status}</span>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => viewRunResults(r.id)} style={{ ...smallBtn, background: '#8e44ad' }}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedRun && runResults.length > 0 && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h3 style={sectionTitle}>Run #{selectedRun} — Detailed Results</h3>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Member</th>
                    <th style={thStyle}>Reference</th>
                    <th style={thStyle}>Obligation</th>
                    <th style={thStyle}>Bank Amount</th>
                    <th style={thStyle}>Shortfall</th>
                    <th style={thStyle}>Excess</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {runResults.map(function(r, i) {
                    return (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={tdStyle}><strong>{r.full_name}</strong></td>
                        <td style={tdStyle}><code style={{ fontSize: '10px' }}>{r.reference_number}</code></td>
                        <td style={tdStyle}>{parseFloat(r.obligation_amount).toLocaleString()}</td>
                        <td style={tdStyle}>{r.bank_csv_amount ? parseFloat(r.bank_csv_amount).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, color: '#e74c3c' }}>{r.shortfall > 0 ? parseFloat(r.shortfall).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, color: '#f39c12' }}>{r.excess > 0 ? parseFloat(r.excess).toLocaleString() : '—'}</td>
                        <td style={tdStyle}>{matchBadge(r.match_status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const btnStyle     = { background: '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };
const smallBtn     = { background: '#2c3e50', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const cardStyle    = { background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const sectionTitle = { margin: '0 0 16px', color: '#2c3e50', fontSize: '15px', fontWeight: 'bold' };
const fieldStyle   = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '180px' };
const labelStyle   = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle   = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const tableStyle   = { width: '100%', borderCollapse: 'collapse' };
const thStyle      = { padding: '9px', textAlign: 'left', fontSize: '12px' };
const tdStyle      = { padding: '9px', borderBottom: '1px solid #eee', fontSize: '12px' };
const alertStyle   = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });

export default Payments;